/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2020. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';

/**
 * @prop {Boolean} modal - Default false. If true, the splash screen will be shown as a modal dialog on top of other content.
 * @prop {String} label - Defines the label of the Splash Screen shown below the logo.
 * @prop {String} version - Defines the version shown below the label.
 * @prop {String} info - Defines the information text shown at the bottom of the screen, such as copyrights information.
 * @prop {String} image - If an image is defined, it will be shown on the right-hand side of the splash screen. It is not shown if the screen is below 48rem wide.
 * @prop {Number} progress - If defined, a Progress bar will be shown instead of authentication inputs. The value of this property can be updated dinamically to represent the live loading status of the application.
 * @slot Holds the authentication inputs, such as Input Text, Dropdown and Checkbox.
 * @slot footer - Holds Buttons used to confirm the login information or similar functionalities.
 * @slot info - Holds the information text shown at the bottom of the screen, such as copyrights information.
 */

export class SHSplashScreen extends LitElement {
  static get properties() {
    return {
      modal: { type: Boolean, reflect: true },
      label: { type: String, reflect: true },
      image: { type: String, reflect: true },
      version: { type: String, reflect: true },
      info: { type: String, reflect: true },
      progress: { type: Number, reflect: true },
      _emptyInfo: { type: Boolean, reflect: true, attribute: 'empty-info' },
    };
  }

  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          position: absolute;
          top: 0rem;
          left: 0rem;
          height: 100%;
          width: 100%;
          z-index: 5;
          align-items: center;
          justify-content: center;
          display: flex;
          background-color: rgba(0, 0, 0, 0.6);
        }

        sh-card {
          height: 100%;
          width: 100%;
        }

        :host([modal]) sh-card {
          height: fit-content;
          height: -moz-fit-content;
          height: -ms-fit-content;
          min-height: 35rem;
          width: 50rem;
          max-width: calc(100% - 1rem);
        }

        .logo {
          padding: 1rem;
          width: fit-content;
          width: -moz-fit-content;
          width: -ms-fit-content;
        }

        .siemens {
          fill: var(--logo-2);
        }

        .healthineers {
          fill: var(--logo-1);
        }

        /* progress bar */
        :host(:not([progress])) sh-progress,
        :host([progress]) .data-wrapper ::slotted(*) {
          display: none;
        }

        .data-wrapper > ::slotted(*) {
          margin-bottom: 0.5rem;
        }

        .data-wrapper {
          width: 30rem;
          max-width: 100%;
          min-height: 23.8125rem;
          margin: 1rem auto 0rem;
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        :host(:not([label])) .label {
          display: none;
        }

        .label {
          text-align: center;
          font-size: 1.875rem;
          margin-top: 1rem;
          word-break: break-word;
        }

        :host(:not([version])) .label {
          margin-bottom: 2rem;
        }

        sh-text[header-2] {
          margin-top: 0.5rem;
          margin-bottom: 2rem;
          max-height: 1.5rem;
          min-height: 1.5rem;
          overflow: hidden;
          text-align: center;
          font: var(--header-2);
          line-height: 1.5rem;
        }

        .content-wrapper {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        :host([image]) .content-wrapper {
          width: calc(50% - 0.5rem);
        }

        /* footer */
        .footer-wrapper {
          width: 100%;
          display: flex;
          margin-top: 1.5rem;
          margin-bottom: 1rem;
          justify-content: flex-end;
          align-items: center;
        }

        .footer-wrapper > ::slotted(*) {
          margin-left: 0.5rem;
        }

        .info {
          display: none;
        }

        .footer-text {
          display: inline-grid;
          align-items: self-end;
          margin-top: 1rem;
          max-height: 2rem;
          height: 2rem;
          overflow: hidden;
        }

        :host([empty-info]) .info {
          display: inline-grid;
          align-items: self-end;
          margin-top: 1rem;
          max-height: 2rem;
          height: 2rem;
          overflow: hidden;
        }

        :host(:not([info])) .info {
          display: none;
        }

        :host([info][empty-info]) .footer-text {
          display: none;
        }

        /* image */
        :host([image]) .image {
          flex: 1;
          margin: -1rem -1rem -1rem 1rem;
          background-size: cover;
          background-repeat: no-repeat;
          background-position: center;
        }

        /* small screens */
        @media only screen and (max-width: 30rem) {
          .logo {
            margin: auto;
          }
        }

        @media only screen and (max-width: 47.9375rem) {
          sh-card {
            height: 100% !important;
            width: 100% !important;
            max-height: unset !important;
            max-width: unset !important;
          }
          .info {
            text-align: center;
          }
          .image {
            display: none;
          }
          .content-wrapper {
            width: 100% !important;
          }
        }
      `,
    ];
  }

  render() {
    return html`
      <sh-card class="card">
        <div style="display: flex; height: 100%;" ?empty-info=${this._emptyInfo}>
          <div class="content-wrapper">
            <div class="logo">
              <svg
                xmlns:svg="http://www.w3.org/2000/svg"
                xmlns="http://www.w3.org/2000/svg"
                version="1.1"
                style="height:3rem"
                viewBox="-1.24339497 -1.24339497 179.20878994 43.93328894"
              >
                <path
                  class="healthineers"
                  d="m 146.66688,22.51125 c 0.23875,0.23875 0.55125,0.35875 0.865,0.35875 0.3125,0 0.62625,-0.12 0.86375,-0.35875 0.24,-0.23875 0.35875,-0.55125 0.35875,-0.865 0,-0.3125 -0.11875,-0.625 -0.35875,-0.865 -0.2375,-0.23875 -0.55125,-0.3575 -0.86375,-0.3575 -0.31375,0 -0.62625,0.11875 -0.865,0.3575 -0.23875,0.24 -0.35875,0.5525 -0.35875,0.865 0,0.31375 0.12,0.62625 0.35875,0.865 m 6.16875,-6.16863 c 0.31,0.31 0.71625,0.465 1.12375,0.465 0.40625,0 0.81374,-0.155 1.12375,-0.465 0.31125,-0.31 0.46499,-0.7175 0.46499,-1.12375 0,-0.4075 -0.15375,-0.81375 -0.46499,-1.12375 -0.31001,-0.31 -0.7175,-0.46625 -1.12375,-0.46625 -0.4075,0 -0.81375,0.15625 -1.12375,0.46625 -0.31001,0.31 -0.465,0.71625 -0.465,1.12375 0,0.40625 0.155,0.81375 0.465,1.12375 m -0.008,12.84888 c 0.31,0.31 0.71625,0.465 1.12375,0.465 0.40625,0 0.81375,-0.155 1.12375,-0.465 0.31125,-0.31 0.465,-0.7175 0.465,-1.12375 0,-0.4075 -0.15376,-0.81375 -0.465,-1.12375 -0.31,-0.31 -0.7175,-0.46625 -1.12375,-0.46625 -0.4075,0 -0.81375,0.15625 -1.12375,0.46625 -0.31,0.31 -0.465,0.71625 -0.465,1.12375 0,0.40625 0.155,0.81375 0.465,1.12375 m 6.09812,-18.93988 c 0.40375,0.40375 0.9325,0.605 1.46125,0.605 0.52875,0 1.0575,-0.20125 1.46125,-0.605 0.40375,-0.40375 0.605,-0.9325 0.605,-1.46125 0,-0.52875 -0.20125,-1.0575 -0.605,-1.46125 -0.40375,-0.40375 -0.9325,-0.605 -1.46125,-0.605 -0.52875,0 -1.0575,0.20125 -1.46125,0.605 -0.40375,0.40375 -0.605,0.9325 -0.605,1.46125 0,0.52875 0.20125,1.0575 0.605,1.46125 m -0.008,12.84875 c 0.40375,0.40375 0.9325,0.605 1.46125,0.605 0.52875,0 1.0575,-0.20125 1.46125,-0.605 0.40375,-0.40375 0.605,-0.9325 0.605,-1.46125 0,-0.52875 -0.20125,-1.0575 -0.605,-1.46125 -0.40375,-0.40375 -0.9325,-0.605 -1.46125,-0.605 -0.52875,0 -1.0575,0.20125 -1.46125,0.605 -0.40375,0.40375 -0.605,0.9325 -0.605,1.46125 0,0.52875 0.20125,1.0575 0.605,1.46125 m -0.007,12.84925 c 0.40375,0.40375 0.9325,0.605 1.46125,0.605 0.52875,0 1.0575,-0.20125 1.46125,-0.605 0.40375,-0.40375 0.605,-0.9325 0.605,-1.46125 0,-0.52875 -0.20125,-1.0575 -0.605,-1.46125 -0.40375,-0.40375 -0.9325,-0.605 -1.46125,-0.605 -0.52875,0 -1.0575,0.20125 -1.46125,0.605 -0.40375,0.40375 -0.605,0.9325 -0.605,1.46125 0,0.52875 0.20125,1.0575 0.605,1.46125 m 5.99688,-18.839 c 0.52375,0.525 1.21125,0.78625 1.89875,0.78625 0.68875,0 1.37625,-0.26125 1.90125,-0.78625 0.52375,-0.52375 0.78625,-1.21125 0.78625,-1.89875 0,-0.6875 -0.2625,-1.375 -0.7875,-1.9 -0.525,-0.525 -1.2125,-0.7875 -1.9,-0.7875 -0.6875,0 -1.375,0.2625 -1.89875,0.78625 -0.525,0.525 -0.7875,1.2125 -0.7875,1.9 0,0.68875 0.2625,1.37625 0.7875,1.9 m -0.006,12.84925 c 0.52375,0.525 1.21125,0.78625 1.89875,0.78625 0.68875,0 1.37625,-0.26125 1.90125,-0.78625 0.52375,-0.525 0.78625,-1.21125 0.78625,-1.89875 0,-0.6875 -0.2625,-1.375 -0.7875,-1.9 -0.525,-0.525 -1.2125,-0.7875 -1.9,-0.7875 -0.6875,0 -1.375,0.26125 -1.89875,0.78625 -0.525,0.525 -0.7875,1.2125 -0.7875,1.9 0,0.68875 0.2625,1.375 0.7875,1.9 m 5.85787,-5.858 c 0.6825,0.6825 1.575,1.02375 2.47,1.02375 0.89375,0 1.78625,-0.34125 2.46875,-1.02375 0.6825,-0.6825 1.02375,-1.57625 1.02375,-2.47 0,-0.89375 -0.34125,-1.78625 -1.02375,-2.46875 -0.6825,-0.6825 -1.575,-1.02375 -2.46875,-1.02375 -0.895,0 -1.7875,0.34125 -2.47,1.02375 -0.6825,0.6825 -1.02375,1.575 -1.02375,2.46875 0,0.89375 0.34125,1.7875 1.02375,2.47"
                  style="fill-opacity:1;fill-rule:nonzero;stroke:none"
                ></path>
                <path
                  class="siemens"
                  d="m 132.43,15.29 0,-3.0175 c 1.70125,0.54125 3.22125,0.81125 4.5575,0.81125 1.82375,0 2.73625,-0.48125 2.73625,-1.445 0,-0.36 -0.13,-0.66125 -0.3875,-0.90625 -0.2725,-0.26 -0.9725,-0.61875 -2.1,-1.07875 -2.0025,-0.82 -3.31625,-1.525 -3.93,-2.11375 -0.79,-0.76375 -1.18625,-1.7375 -1.18625,-2.92375 0,-1.5025 0.57375,-2.65 1.7275,-3.44 C 134.9825,0.3925 136.46625,0 138.295,0 c 1.02625,0 2.3575,0.16375 3.99,0.49125 l 0.3775,0.065 0,2.90125 c -1.41875,-0.56625 -2.7375,-0.84875 -3.96125,-0.84875 -1.705,0 -2.5575,0.47 -2.5575,1.41375 0,0.35125 0.1725,0.63875 0.51625,0.8625 0.27375,0.17125 1.07,0.5525 2.38375,1.14375 1.85,0.82625 3.08875,1.545 3.71125,2.1575 0.735,0.72625 1.1025,1.665 1.1025,2.8175 0,1.65375 -0.7175,2.91625 -2.1475,3.78625 -1.1725,0.70375 -2.68875,1.05625 -4.5425,1.05625 -1.56875,0 -3.14875,-0.185 -4.7375,-0.55625 m -6.0335,-15.02513 0,10.22375 -5.24375,-10.22375 -4.945,0 0,15.2725 3.005,0 0,-10.36 5.385,10.36 4.80375,0 0,-15.2725 -3.005,0 z m -24.11275,0 0,15.2725 11.1175,0 0,-2.91875 -7.00625,0 0,-3.62375 5.945,0 0,-2.51875 -5.945,0 0,-3.4475 6.82875,0 0,-2.76375 -10.94,0 z m -9.16475,0 -3.94,9.7575 -3.84125,-9.7575 -5.53125,0 0,15.2725 3.005,0 0,-10.8125 4.3925,10.96625 2.645,0 4.47875,-10.96625 0,10.8125 4.04375,0 0,-15.2725 -5.2525,0 z m -27.214,0 0,15.2725 11.11625,0 0,-2.91875 -7.005,0 0,-3.62375 5.94375,0 0,-2.51875 -5.94375,0 0,-3.4475 6.82875,0 0,-2.76375 -10.94,0 z m -8.1775,15.27163 2.1325,0 2.13375,0 0,-7.63625 0,-7.63625 -2.13375,0 -2.1325,0 0,7.63625 0,7.63625 z M 43.89188,15.29 l 0,-3.0175 c 1.71624,0.54125 3.23499,0.81125 4.55749,0.81125 1.82375,0 2.73626,-0.48125 2.73626,-1.445 0,-0.36 -0.13376,-0.66125 -0.39875,-0.90625 -0.27376,-0.26 -0.96875,-0.61875 -2.09001,-1.07875 C 46.68813,8.8275 45.37563,8.1225 44.76562,7.54 43.97812,6.77 43.58187,5.8 43.58187,4.62625 c 0,-1.50875 0.57625,-2.66 1.7275,-3.45 C 46.44563,0.3925 47.92813,0 49.75437,0 c 1.00751,0 2.46501,0.185 4.37,0.55625 l 0,2.90125 c -1.41749,-0.56625 -2.735,-0.84875 -3.95,-0.84875 -1.71375,0 -2.56999,0.47 -2.56999,1.41375 0,0.35125 0.17374,0.63875 0.51875,0.8625 0.28749,0.17875 1.07874,0.56 2.3725,1.14375 1.86249,0.82625 3.10499,1.545 3.72125,2.1575 0.73374,0.72625 1.10249,1.665 1.10249,2.8175 0,1.65375 -0.71999,2.91625 -2.15999,3.78625 -1.16375,0.70375 -2.67626,1.05625 -4.53125,1.05625 -1.5675,0 -3.14626,-0.185 -4.73625,-0.55625"
                  style="fill-opacity:1;fill-rule:nonzero;stroke:none"
                ></path>
                <path
                  class="healthineers"
                  d="m 56.56775,30.56875 3.44875,0 0,-2.87 -3.44875,0 0,-4.15875 -3.65875,0 0,4.16 -2.03,0 0,2.87 2.03,0 0,6.5 c 0,2.2125 0.21,4.37 4.00125,4.37 1.15875,0 2.5525,-0.34125 3.34375,-0.84125 l -0.79125,-2.45 c -0.5775,0.2375 -1.13125,0.4225 -1.60625,0.4225 -0.97375,0 -1.28875,-0.58 -1.28875,-2.0275 l 0,-5.975 z m 86.37162,-2.10438 0.01,-0.0312 c -1.165,-0.7625 -2.6925,-1.2225 -4.35875,-1.135 -2.705,0.14125 -4.83125,1.75375 -4.68375,4.54 0.14625,2.78875 2.17875,3.44625 4.17875,3.92125 1.14875,0.2825 2.10875,0.4975 2.1625,1.5225 0.0525,1 -0.6375,1.43125 -1.60875,1.4825 -1.19,0.0612 -2.695,-0.4675 -3.665,-0.96625 l -1.09126,2.51 c 0.76876,0.51375 2.74875,1.24625 5.08,1.125 3.04625,-0.16 5.04,-1.76375 4.885,-4.73625 -0.14375,-2.735 -2.33125,-3.3325 -4.28124,-3.8375 -1.20125,-0.305 -2.08625,-0.6025 -2.14125,-1.6275 -0.045,-0.86875 0.6475,-1.2475 1.515,-1.29375 0.89875,-0.0462 1.86625,0.3475 2.89375,1.0675 l 1.105,-2.54125 z m -14.82687,4.289 c 0.0525,-1.39375 0.895,-2.29 2.21125,-2.29 0.68375,0 1.3175,0.18375 1.8675,0.47375 L 132.85,27.77837 c -0.63125,-0.3425 -1.44625,-0.47375 -2.185,-0.47375 -1.23625,0 -2.52625,0.73625 -3.1575,2.29 l -0.3425,-1.89625 -2.975,0 0.26375,2.3175 0,11.02875 3.65875,0 0,-8.29125 z m -40.03688,8.29188 0,-8.555 c 0,-1.26375 0.89626,-2.36875 2.29001,-2.36875 1.39625,0 1.63124,1.07875 1.63124,2.52625 l 0,8.3975 3.65875,0 0,-9.10875 c 0,-3.1575 -1.57875,-4.6325 -4.18374,-4.6325 -1.76376,0 -3.08,0.73875 -3.97501,1.975 l -0.26375,-1.58 -3.05375,0 0.23751,2.475 0,10.87125 3.65874,0 z M 77.35125,23.52912 c 0,0.555 0.19375,1.025 0.53875,1.3575 0.345,0.33125 0.84125,0.525 1.4475,0.525 0.5925,0 1.09625,-0.19375 1.45,-0.525 0.355,-0.3325 0.56125,-0.8025 0.56125,-1.3575 0,-0.51625 -0.20625,-0.98625 -0.56125,-1.32875 -0.35375,-0.34125 -0.8575,-0.55375 -1.45,-0.55375 -0.59375,0 -1.09,0.2125 -1.4375,0.55375 -0.34875,0.3425 -0.54875,0.8125 -0.54875,1.32875 m 0.16812,17.5165 1.83001,0 1.82875,0 0,-6.6725 0,-6.67375 -1.82875,0 -1.83001,0 0,6.67375 0,6.6725 z m -10.8175,-3.7e-4 0,-8.6075 c 0,-1.2375 0.89501,-2.31625 2.2375,-2.31625 1.39501,0 1.68376,1.0525 1.68376,2.42125 l 0,8.5025 3.65875,0 0,-9.05375 c 0,-3.13375 -1.5,-4.6875 -4.26376,-4.6875 -1.29,0 -2.47375,0.47375 -3.31625,1.36875 l 0,-5.1325 -3.65874,0 0,17.505 3.65874,0 z m -22.82162,-17.4785 0,13.50375 c 0,2.21125 0.21,4.37 4.00125,4.37 1.15875,0 2.5525,-0.34125 3.3425,-0.8425 l -0.79,-2.44875 c -0.57875,0.2375 -1.13125,0.4225 -1.60375,0.4225 -0.97625,0 -1.29125,-0.58 -1.29125,-2.0275 l 0,-12.9775 -3.65875,0 z m -3.05338,17.4785 -0.26374,-2.6575 0,-10.11 c -2,-0.73625 -3.94876,-0.97375 -4.84375,-0.97375 -4.65751,0 -6.97501,3.0275 -6.97501,7.66125 0,3.5525 1.29001,6.44875 5.10625,6.44875 1.4225,0 2.7375,-0.52625 3.52751,-1.7375 l 0.1325,0.0275 0.18374,1.34125 3.1325,0 z M 36.93063,36.7815 c 0,0.97375 -0.78875,1.97375 -2.15751,1.97375 -1.94874,0 -2.39625,-1.86875 -2.39625,-4.00125 0,-2.58 0.7375,-4.7375 2.89625,-4.7375 0.68376,0 1.315,0.13125 1.65751,0.28875 l 0,6.47625 z M 9.13375,23.54075 l 0,6.975 -5.37,0 0,-6.975 -3.76375,0 0,17.505 3.76375,0 0,-7.23875 5.37,0 0,7.23875 3.765,0 0,-17.505 -3.765,0 z m 17.50512,7.60712 c 0,-2.5 -1.71125,-3.84375 -4.37,-3.84375 -4.31624,0 -6.55375,3.52875 -6.55375,7.3975 0,3.94875 1.79001,6.73875 6.26376,6.73875 2.65999,0 4.5025,-1.21125 4.8175,-1.47375 l -1.0525,-2.4475 c -0.73625,0.49875 -1.86875,1.13125 -3.29001,1.13125 -2.00124,0 -2.84375,-1.34375 -3.02749,-2.60625 3.185,-0.13125 7.21249,-0.9225 7.21249,-4.89625 m -3.21125,0.0275 c 0,1.815 -2.44874,2.23625 -4.15875,2.29 0.0775,-1.8175 1.02625,-3.58125 2.81626,-3.58125 0.815,0 1.34249,0.5 1.34249,1.29125 m 98.18325,-0.0275 c 0,-2.5 -1.71125,-3.84375 -4.37,-3.84375 -4.31624,0 -6.55375,3.52875 -6.55375,7.3975 0,3.94875 1.79,6.73875 6.26375,6.73875 2.66,0 4.5025,-1.21125 4.8175,-1.47375 l -1.05375,-2.4475 c -0.73625,0.49875 -1.8675,1.13125 -3.28874,1.13125 -2.00125,0 -2.8425,-1.34375 -3.02751,-2.60625 3.18625,-0.13125 7.2125,-0.9225 7.2125,-4.89625 m -3.21124,0.0275 c 0,1.815 -2.44875,2.23625 -4.15875,2.29 0.0775,-1.8175 1.02625,-3.58125 2.81625,-3.58125 0.815,0 1.3425,0.5 1.3425,1.29125 m -9.55525,-0.0275 c 0,-2.5 -1.71125,-3.84375 -4.37,-3.84375 -4.31625,0 -6.55376,3.52875 -6.55376,7.3975 0,3.94875 1.79,6.73875 6.26376,6.73875 2.66,0 4.5025,-1.21125 4.8175,-1.47375 l -1.0525,-2.4475 c -0.7375,0.49875 -1.86875,1.13125 -3.29,1.13125 -2.00125,0 -2.84375,-1.34375 -3.0275,-2.60625 3.18625,-0.13125 7.2125,-0.9225 7.2125,-4.89625 m -3.21125,0.0275 c 0,1.815 -2.44875,2.23625 -4.15875,2.29 0.0775,-1.8175 1.02625,-3.58125 2.81625,-3.58125 0.815,0 1.3425,0.5 1.3425,1.29125"
                  style="fill-opacity:1;fill-rule:nonzero;stroke:none"
                ></path>
              </svg>
            </div>
            <div class="data-wrapper">
              <sh-text class="label" size="super-header" color="primary">${this.label}</sh-text>
              ${this.version
                ? html` <sh-text class="version" header-2>${this.version}</sh-text> `
                : ''}
              <slot></slot>
              ${this.progress
                ? html`
                    <sh-progress
                      class="progress"
                      show-progress
                      value="${this.progress}"
                    ></sh-progress>
                  `
                : ''}
              <div class="footer-wrapper">
                <slot name="footer" class="footer"></slot>
              </div>
            </div>
            ${this.info
              ? html` <sh-text class="info" size="body-2" color="secondary">${this.info}</sh-text> `
              : ''}
            <div class="footer-text">
              <slot
                name="info"
                id="info"
                class="info_slot"
                @slotchange="${() => this._handleSlotNodes()}"
              ></slot>
            </div>
          </div>
          ${this.image
            ? html` <div class="image" style="background-image: ${this.image}"></div> `
            : ''}
        </div>
      </sh-card>
    `;
  }

  constructor() {
    super();
    this.label = 'Product Name';
    this.modal = false;
  }
  updated(changedProperties) {
    const listOfProperties = ['label', 'modal', 'info', 'version', 'progress', 'image'];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }
  firstUpdated() {
    this._handleSlotNodes();
  }
  _handleSlotNodes() {
    const infoNodes = this.shadowRoot.querySelector('#info').assignedNodes({
      flatten: true,
    }).length;
    this._emptyInfo = infoNodes === 0;
  }
}

customElements.define('sh-splash-screen', SHSplashScreen);
